import React, { useState, useRef, useContext, useEffect } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Download, Upload, FileSpreadsheet, FileText, 
  Package, Users, History, AlertCircle, CheckCircle2,
  Database, HardDrive, RefreshCw, Archive, CloudUpload, Shield,
  Loader2, Image, AlertTriangle
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ExportImport = () => {
  const { user } = useContext(AuthContext);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const backupFileInputRef = useRef(null);
  const verifyFileInputRef = useRef(null);
  
  // Backup states
  const [backupStatus, setBackupStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [migratingPhotos, setMigratingPhotos] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [selectedBackupFile, setSelectedBackupFile] = useState(null);
  const [backupSummary, setBackupSummary] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadBackupStatus();
    }
  }, [user]);

  const loadBackupStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data } = await axios.get(`${API}/backup/status`);
      setBackupStatus(data);
    } catch (error) {
      console.error('Error loading backup status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleExport = async (type, format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/export/${type}/${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }
      
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${type}_export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=(.+)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Export ${format.toUpperCase()} téléchargé avec succès`);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'export');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/export/template/tools`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_outils.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Modèle téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du modèle');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API}/export/tools/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(data);
      
      if (data.imported > 0) {
        toast.success(`${data.imported} outils importés avec succès`);
      }
      
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} erreurs lors de l'import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
      setImportResult({ imported: 0, errors: [error.response?.data?.detail || 'Erreur inconnue'] });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleMigratePhotos = async () => {
    setMigratingPhotos(true);
    try {
      const { data } = await axios.post(`${API}/backup/migrate-photos`);
      toast.success(data.message);
      loadBackupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la migration');
    } finally {
      setMigratingPhotos(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    setBackupSummary(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/backup/create`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la création de la sauvegarde');
      }
      
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'tooltracker_backup.zip';
      
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=(.+)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      // Read embedded manifest from response header
      const manifestB64 = response.headers.get('X-Backup-Manifest');
      let summary = null;
      if (manifestB64) {
        try {
          const json = atob(manifestB64);
          summary = JSON.parse(decodeURIComponent(escape(json)));
        } catch {
          try { summary = JSON.parse(atob(manifestB64)); } catch { summary = null; }
        }
      }
      summary = summary || {};
      summary.filename = filename;
      summary.downloaded_bytes = blob.size;
      setBackupSummary(summary);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Sauvegarde créée et téléchargée');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création de la sauvegarde');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleVerifyBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setVerifying(true);
    setVerifyResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const { data } = await axios.post(`${API}/backup/verify`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setVerifyResult(data);
      toast.success('Vérification terminée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
      setVerifyResult({ error: error.response?.data?.detail || 'Erreur inconnue' });
    } finally {
      setVerifying(false);
      if (verifyFileInputRef.current) {
        verifyFileInputRef.current.value = '';
      }
    }
  };

  const handleRestoreBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Store file and show confirmation dialog
    setSelectedBackupFile(file);
    setConfirmRestoreOpen(true);
  };

  const executeRestore = async () => {
    if (!selectedBackupFile) return;
    
    setConfirmRestoreOpen(false);
    setRestoringBackup(true);
    setRestoreResult(null);
    
    const formData = new FormData();
    formData.append('file', selectedBackupFile);

    try {
      const { data } = await axios.post(`${API}/backup/restore?clear_existing=true`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setRestoreResult(data);
      toast.success('Restauration terminée avec succès');
      loadBackupStatus();
    } catch (error) {
      console.error('Restore error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
      setRestoreResult({ 
        success: false, 
        stats: { errors: [error.response?.data?.detail || 'Erreur inconnue'] } 
      });
    } finally {
      setRestoringBackup(false);
      setSelectedBackupFile(null);
      if (backupFileInputRef.current) {
        backupFileInputRef.current.value = '';
      }
    }
  };

  const cancelRestore = () => {
    setConfirmRestoreOpen(false);
    setSelectedBackupFile(null);
    if (backupFileInputRef.current) {
      backupFileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
          Export & Import
        </h1>
        <p className="text-muted-foreground mt-2">
          Exportez vos données, importez des outils ou gérez vos sauvegardes
        </p>
      </div>

      {/* Backup Section - Admin only - NEW */}
      {user?.role === 'admin' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-heading font-semibold flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              Sauvegarde Complète
            </h2>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadBackupStatus}
              disabled={loadingStatus}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingStatus ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Database className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">État de la base</CardTitle>
                    <CardDescription>Données actuelles dans MongoDB</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingStatus ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : backupStatus ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Utilisateurs:</span>
                        <Badge variant="secondary" className="ml-2">{backupStatus.collections?.users || 0}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Collaborateurs:</span>
                        <Badge variant="secondary" className="ml-2">{backupStatus.collections?.collaborators || 0}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Outils:</span>
                        <Badge variant="secondary" className="ml-2">{backupStatus.collections?.tools || 0}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Affectations:</span>
                        <Badge variant="secondary" className="ml-2">{backupStatus.collections?.assignments || 0}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Kits:</span>
                        <Badge variant="secondary" className="ml-2">{backupStatus.collections?.kits || 0}</Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Locations:</span>
                        <Badge variant="secondary" className="ml-2">{backupStatus.collections?.rental_equipment || 0}</Badge>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Photos (GridFS):
                        </span>
                        <span className="font-medium">
                          {backupStatus.total_photos} fichiers ({backupStatus.gridfs?.total_size_mb || 0} MB)
                        </span>
                      </div>
                      
                      {backupStatus.unmigrated_photos > 0 && (
                        <Alert className="mt-3 border-amber-500 bg-amber-50">
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                          <AlertDescription className="text-amber-800">
                            {backupStatus.unmigrated_photos} photo(s) non migrées vers GridFS
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Impossible de charger le statut</p>
                )}
              </CardContent>
            </Card>

            {/* Actions Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <HardDrive className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Actions</CardTitle>
                    <CardDescription>Gérer les sauvegardes et migrations</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Migrate Photos */}
                {backupStatus?.unmigrated_photos > 0 && (
                  <Button 
                    onClick={handleMigratePhotos}
                    disabled={migratingPhotos}
                    variant="outline"
                    className="w-full rounded-full"
                  >
                    {migratingPhotos ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CloudUpload className="w-4 h-4 mr-2" />
                    )}
                    Migrer les photos vers GridFS
                  </Button>
                )}
                
                {/* Create Backup */}
                <Button 
                  onClick={handleCreateBackup}
                  disabled={creatingBackup}
                  className="w-full rounded-full"
                >
                  {creatingBackup ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Archive className="w-4 h-4 mr-2" />
                  )}
                  {creatingBackup ? 'Création en cours...' : 'Créer une sauvegarde ZIP'}
                </Button>
                
                {/* Restore Backup */}
                <div>
                  <input
                    ref={backupFileInputRef}
                    type="file"
                    accept=".zip,.json,application/zip,application/json"
                    onChange={handleRestoreBackup}
                    className="hidden"
                  />
                  <Button 
                    onClick={() => backupFileInputRef.current?.click()}
                    disabled={restoringBackup}
                    variant="destructive"
                    className="w-full rounded-full"
                  >
                    {restoringBackup ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {restoringBackup ? 'Restauration en cours...' : 'Restaurer depuis une sauvegarde'}
                  </Button>
                </div>
                
                {/* Verify Backup */}
                <div>
                  <input
                    ref={verifyFileInputRef}
                    type="file"
                    accept=".zip,application/zip"
                    onChange={handleVerifyBackup}
                    className="hidden"
                    data-testid="verify-backup-input"
                  />
                  <Button
                    data-testid="verify-backup-btn"
                    onClick={() => verifyFileInputRef.current?.click()}
                    disabled={verifying}
                    variant="outline"
                    className="w-full rounded-full"
                  >
                    {verifying ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    {verifying ? 'Vérification...' : 'Vérifier une sauvegarde (sans restaurer)'}
                  </Button>
                </div>
                
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    La sauvegarde ZIP inclut toutes les données et photos. 
                    Conservez ce fichier en lieu sûr pour pouvoir restaurer en cas de problème.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>

          {/* Restore Result */}
          {restoreResult && (
            <Card className={restoreResult.success ? "border-green-500" : "border-red-500"}>
              <CardContent className="pt-6">
                {restoreResult.success ? (
                  <Alert className="border-green-500 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">Restauration réussie</AlertTitle>
                    <AlertDescription className="text-green-700">
                      <div className="mt-2 space-y-1 text-sm">
                        {Object.entries(restoreResult.stats?.collections || {}).map(([key, value]) => (
                          <div key={key}>{key}: {value} éléments restaurés</div>
                        ))}
                        <div>Photos: {restoreResult.stats?.photos || 0} fichiers restaurés</div>
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Erreurs lors de la restauration</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc pl-4 mt-2 space-y-1">
                        {restoreResult.stats?.errors?.slice(0, 5).map((error, idx) => (
                          <li key={idx} className="text-sm">{error}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Backup Summary - shown right after creating a backup */}
          {backupSummary && (
            <Card className="border-green-500" data-testid="backup-summary-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-5 h-5" />
                  Sauvegarde créée : {backupSummary.filename}
                </CardTitle>
                <CardDescription>
                  Taille : {((backupSummary.downloaded_bytes || backupSummary.zip_size_bytes || 0) / (1024 * 1024)).toFixed(2)} MB
                  {backupSummary.data_sha256 && (
                    <> · Empreinte : <code className="text-xs">{backupSummary.data_sha256.slice(0, 12)}…</code></>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {Object.entries(backupSummary.collections_counts || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <span className="text-muted-foreground capitalize">{key}:</span>
                      <Badge variant="secondary">{value}</Badge>
                    </div>
                  ))}
                  <div className="flex items-center justify-between p-2 bg-blue-50 rounded col-span-2 md:col-span-3">
                    <span className="text-blue-700 font-medium flex items-center gap-2">
                      <Image className="w-4 h-4" /> Photos sauvegardées :
                    </span>
                    <Badge className="bg-blue-600">
                      {backupSummary.photos_count || 0} 
                      {backupSummary.photos_size_bytes ? ` (${(backupSummary.photos_size_bytes / (1024 * 1024)).toFixed(1)} MB)` : ''}
                    </Badge>
                  </div>
                </div>
                {backupSummary.errors_count > 0 && (
                  <Alert variant="destructive" className="mt-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {backupSummary.errors_count} erreur(s) lors de la création — certaines données peuvent être incomplètes.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Verify Result */}
          {verifyResult && !verifyResult.error && (
            <Card className="border-blue-500" data-testid="verify-result-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <Shield className="w-5 h-5" />
                  Analyse de la sauvegarde : {verifyResult.filename}
                </CardTitle>
                <CardDescription>
                  Taille : {((verifyResult.size_bytes || 0) / (1024 * 1024)).toFixed(2)} MB
                  {verifyResult.truncated && <span className="text-red-600 font-medium"> · ⚠️ FICHIER TRONQUÉ</span>}
                  {verifyResult.recovery_used && <span className="text-orange-600 font-medium"> · Récupération auto</span>}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Integrity */}
                {verifyResult.integrity_ok !== null && (
                  <div className={`p-3 rounded-lg flex items-center gap-2 ${verifyResult.integrity_ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {verifyResult.integrity_ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span className="text-sm font-medium">
                      Intégrité du fichier : {verifyResult.integrity_ok ? 'OK (hash vérifié)' : 'CORROMPU (hash différent)'}
                    </span>
                  </div>
                )}

                {/* Photos completeness */}
                {verifyResult.expected_photos !== null && verifyResult.expected_photos !== undefined && (
                  <div className={`p-3 rounded-lg flex items-center justify-between ${verifyResult.photos_complete ? 'bg-green-50' : 'bg-orange-50'}`}>
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Photos dans le ZIP
                    </span>
                    <span className="text-sm font-mono">
                      {verifyResult.zip_photos_count} / {verifyResult.expected_photos} attendues
                      {!verifyResult.photos_complete && verifyResult.expected_photos > verifyResult.zip_photos_count && (
                        <Badge variant="destructive" className="ml-2">
                          {verifyResult.expected_photos - verifyResult.zip_photos_count} manquante(s)
                        </Badge>
                      )}
                    </span>
                  </div>
                )}

                {/* Counts comparison */}
                <div>
                  <h4 className="text-sm font-medium mb-2">Comparaison ZIP vs base actuelle</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Collection</th>
                          <th className="text-right p-2">Dans le ZIP</th>
                          <th className="text-right p-2">En base</th>
                          <th className="text-right p-2">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(verifyResult.zip_counts || {}).map((k) => {
                          const delta = verifyResult.deltas?.[k] || 0;
                          return (
                            <tr key={k} className="border-t">
                              <td className="p-2 capitalize">{k}</td>
                              <td className="text-right p-2 font-mono">{verifyResult.zip_counts[k]}</td>
                              <td className="text-right p-2 font-mono">{verifyResult.db_counts?.[k] || 0}</td>
                              <td className={`text-right p-2 font-mono ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                {delta > 0 ? `+${delta}` : delta}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="border-t bg-blue-50/50">
                          <td className="p-2 font-medium">Photos</td>
                          <td className="text-right p-2 font-mono">{verifyResult.zip_photos_count}</td>
                          <td className="text-right p-2 font-mono">{verifyResult.db_photos_count}</td>
                          <td className={`text-right p-2 font-mono ${verifyResult.photos_delta > 0 ? 'text-green-600' : verifyResult.photos_delta < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {verifyResult.photos_delta > 0 ? `+${verifyResult.photos_delta}` : verifyResult.photos_delta}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Manifest details */}
                {verifyResult.manifest && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      Voir le manifeste complet
                    </summary>
                    <pre className="mt-2 p-2 bg-muted/30 rounded overflow-x-auto">
                      {JSON.stringify(verifyResult.manifest, null, 2)}
                    </pre>
                  </details>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Divider */}
      {user?.role === 'admin' && <div className="border-t pt-6" />}

      {/* Export Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-heading font-semibold">Export de données</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Tools Export */}
          <Card data-testid="export-tools-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Outils</CardTitle>
                  <CardDescription>Inventaire complet des outils</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                data-testid="export-tools-csv"
                onClick={() => handleExport('tools', 'csv')}
                variant="outline"
                className="w-full rounded-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                data-testid="export-tools-excel"
                onClick={() => handleExport('tools', 'excel')}
                className="w-full rounded-full"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </CardContent>
          </Card>

          {/* Collaborators Export */}
          <Card data-testid="export-collaborators-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Collaborateurs</CardTitle>
                  <CardDescription>Liste des collaborateurs</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                data-testid="export-collaborators-csv"
                onClick={() => handleExport('collaborators', 'csv')}
                variant="outline"
                className="w-full rounded-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                data-testid="export-collaborators-excel"
                onClick={() => handleExport('collaborators', 'excel')}
                className="w-full rounded-full"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </CardContent>
          </Card>

          {/* History Export */}
          <Card data-testid="export-history-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <History className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Historique</CardTitle>
                  <CardDescription>Historique des affectations</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                data-testid="export-history-csv"
                onClick={() => handleExport('history', 'csv')}
                variant="outline"
                className="w-full rounded-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                data-testid="export-history-excel"
                onClick={() => handleExport('history', 'excel')}
                className="w-full rounded-full"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Import Section - Admin only */}
      {user?.role === 'admin' && (
        <div className="space-y-4 pt-6 border-t">
          <h2 className="text-2xl font-heading font-semibold">Import d'outils</h2>
          
          <Card data-testid="import-tools-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Upload className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Import en masse</CardTitle>
                  <CardDescription>
                    Importez plusieurs outils depuis un fichier CSV
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Format du fichier CSV</AlertTitle>
                <AlertDescription>
                  Le fichier doit contenir les colonnes suivantes (séparateur: point-virgule) :
                  <br />
                  <code className="text-xs bg-muted px-1 py-0.5 rounded mt-1 inline-block">
                    Nom;Type;Numéro d'article;N° de commande;Fournisseur;Quantité totale;Date d'expiration
                  </code>
                </AlertDescription>
              </Alert>
              
              <div className="flex gap-3">
                <Button 
                  data-testid="download-template-btn"
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  className="rounded-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le modèle
                </Button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleImport}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <Button 
                  data-testid="import-tools-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="rounded-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? 'Import en cours...' : 'Importer un fichier CSV'}
                </Button>
              </div>

              {/* Import Results */}
              {importResult && (
                <div className="space-y-3 mt-4">
                  {importResult.imported > 0 && (
                    <Alert variant="default" className="border-green-500 bg-green-50">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-800">Import réussi</AlertTitle>
                      <AlertDescription className="text-green-700">
                        {importResult.imported} outil(s) importé(s) avec succès
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {importResult.errors?.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Erreurs lors de l'import</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc pl-4 mt-2 space-y-1">
                          {importResult.errors.slice(0, 10).map((error, idx) => (
                            <li key={idx} className="text-sm">{error}</li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-sm font-medium">
                              ... et {importResult.errors.length - 10} autres erreurs
                            </li>
                          )}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Restore Confirmation Dialog */}
      <AlertDialog open={confirmRestoreOpen} onOpenChange={setConfirmRestoreOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirmer la restauration
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p className="font-semibold text-destructive">
                ⚠️ ATTENTION : Cette action est irréversible !
              </p>
              <p>
                Toutes les données actuelles seront SUPPRIMÉES et remplacées par celles de la sauvegarde :
              </p>
              <ul className="list-disc pl-6 text-sm">
                <li>Utilisateurs</li>
                <li>Collaborateurs</li>
                <li>Outils</li>
                <li>Affectations</li>
                <li>Locations</li>
                <li>Photos</li>
              </ul>
              {selectedBackupFile && (
                <p className="text-sm mt-2">
                  Fichier sélectionné : <strong>{selectedBackupFile.name}</strong>
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelRestore}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={executeRestore} className="bg-destructive hover:bg-destructive/90">
              Oui, restaurer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExportImport;
